import React, { useState, useEffect, useRef } from "react";
import { CropOverlay } from "./CropOverlay";
import { Toast } from "src/components";
import { CropperWrapper } from "./VideoCrop.style";
import {
    ImagePickerLabel,
    ImageButton,
} from "../../components/ImageCropModal/ModalCrop.style";
import { captureFramesEverySecond } from "./CropperHelper";
import { OriginalVideoCropper } from "./OriginalCropperView";
import { LottieLoadingIndicator } from "src/components";

export const CropperSection = ({
    videoSrcInternal,
    crop,
    zoom,
    setCrop,
    setZoom,
    computedAspect,
    handleCropComplete,
    handleCropSizeChange,
    cropBoxSize,
    setVideoSrcInternal,
    videoRef,
    trimmedVideoSrc,
    setTrimmedVideoSrc,
    trimStart,
    setTrimStart,
    trimEnd,
    setTrimEnd,
    setCropBoxSize
}) => {
    const hiddenVideoRef = useRef(null);
    const progressBarRef = useRef(null);

    // Crop & trim state
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isTrimming, setIsTrimming] = useState(false);
    const [showTrimmer, setShowTrimmer] = useState(false);
    const [capturedFrames, setCapturedFrames] = useState([]);
    const [isVideoLoading, setIsVideoLoading] = useState(true);
    const [cropperKey, setCropperKey] = useState(Date.now());

    // Load hidden video and capture frames automatically
    useEffect(() => {
        const hiddenVideo = hiddenVideoRef.current;
        if (!hiddenVideo || !videoSrcInternal) return;

        hiddenVideo.src = videoSrcInternal;
        hiddenVideo.load();

        const handleLoadedMetadata = () => {
            const videoDuration = hiddenVideo.duration;
            setDuration(videoDuration);

            // Only reset trimStart and trimEnd if no trimmed video source exists
            if (!trimmedVideoSrc) {
                setTrimStart(0);
                setTrimEnd(videoDuration);
            }

            captureFramesEverySecond(hiddenVideo, videoDuration, cropBoxSize)
                .then(setCapturedFrames)
                .catch((err) => {
                    console.error("Error capturing frames:", err);
                    setCapturedFrames([]);
                })
                .finally(() => {
                    setIsVideoLoading?.(false);
                });
        };

        hiddenVideo.addEventListener("loadedmetadata", handleLoadedMetadata);

        return () => {
            hiddenVideo.removeEventListener("loadedmetadata", handleLoadedMetadata);
        };
    }, [videoSrcInternal, cropBoxSize, trimmedVideoSrc]);

    
    // Set up videoRef (visible video) events
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;

        const onTimeUpdate = () => {
            const current = v.currentTime;

            if (current >= trimEnd) {
                v.pause();
                setIsPlaying(false);
                setCurrentTime(trimEnd);
            } else {
                setCurrentTime(current);
            }
        };

        const onLoadedMetadata = () => {
            setDuration(v.duration);
            setTrimEnd(v.duration);
        };

        const onEnded = () => {
            v.pause();
            setIsPlaying(false);
            setCurrentTime(0);
        };

        v.addEventListener("timeupdate", onTimeUpdate);
        v.addEventListener("loadedmetadata", onLoadedMetadata);
        v.addEventListener("ended", onEnded);

        return () => {
            v.removeEventListener("timeupdate", onTimeUpdate);
            v.removeEventListener("loadedmetadata", onLoadedMetadata);
            v.removeEventListener("ended", onEnded);
        };
    }, [trimEnd]);


    // Stop playback on new video
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        v.pause();
        v.currentTime = 0;
        setIsPlaying(false);
    }, [videoSrcInternal]);

    const formatTime = (sec) => {
        const rounded = Math.round(sec);
        const m = String(Math.floor(rounded / 60)).padStart(2, "0");
        const s = String(rounded % 60).padStart(2, "0");
        return `${m}:${s}`;
    };

    const handleTrim = async () => {
        setIsTrimming(true);
        try {
            const newTrimmedUrl = videoSrcInternal;
            setTrimmedVideoSrc(newTrimmedUrl);
            setShowTrimmer(false);

            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
            }
            setIsPlaying(false);
            Toast.success("Trim applied.");
        } catch (err) {
            console.error("Trimming failed:", err);
            Toast.error("Trimming failed. Please try again.");
        } finally {
            setIsTrimming(false);
        }
    };

    const togglePlay = () => {
        const v = videoRef.current;
        if (!v) return;

        if (v.paused) {
            const buffer = 0.05;
            if (v.currentTime < trimStart || v.currentTime >= trimEnd - buffer) {
                v.currentTime = trimStart;
            }
            v.play();
            setIsPlaying(true);
        } else {
            v.pause();
            setIsPlaying(false);
        }
    };

    const handleTrimDrag = (e, type) => {
        const bar = progressBarRef.current;
        const v = videoRef.current;

        if (!bar || !v || duration === 0) return;

        const rect = bar.getBoundingClientRect();

        // Always pause the video and reset to trimStart when drag starts
        v.pause();

        let newTrimStart = trimStart;
        let newTrimEnd = trimEnd;

        const onMove = (moveEvent) => {
            let x = moveEvent.clientX - rect.left;
            x = Math.max(0, Math.min(x, rect.width));
            const time = (x / rect.width) * duration;

            if (type === "start") {
                newTrimStart = Math.min(time, trimEnd - 0.1);
                setTrimStart(newTrimStart);
            } else {
                newTrimEnd = Math.max(time, trimStart + 0.1);
                setTrimEnd(newTrimEnd);
            }

            // Reset video while dragging
            const shouldReset =
            newTrimStart > 0 || newTrimEnd < duration;

            if (shouldReset) {
                v.pause();
                v.currentTime = newTrimStart;
                setIsPlaying(false);
                setCurrentTime(newTrimStart);
            }
        };

        const onUp = () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };

        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
    };


    const onSelectVideo = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (videoSrcInternal) {
            URL.revokeObjectURL(videoSrcInternal);
        }

        const url = URL.createObjectURL(file);
        setCropperKey(Date.now());
        setVideoSrcInternal(url);
        setTrimmedVideoSrc(null);
        setCrop({ x: 0, y: 0 });
        setCropBoxSize({ width: 0, height: 0 });
        setZoom(1);
        setShowTrimmer(false);
        setIsTrimming(false);
        setCapturedFrames([]);
    };

    return (
        <CropperWrapper>
            {isVideoLoading && (
                <LottieLoadingIndicator 
                    style={{ 
                        position: "absolute",
                        top: 0,
                        left: 0, 
                        bottom: 0, 
                        right: 0, 
                        backgroundColor: "rgba(255,255,255,0.85)", 
                        display: "flex", 
                        alignItems: "center", 
                        justifyContent: "center",
                        zIndex: 99999, 
                        fontWeight: 500 }}
                />
            )}
            {/* File input */}
            <ImagePickerLabel htmlFor="video-picker">
                <input
                    id="video-picker"
                    type="file"
                    accept="video/*"
                    style={{ display: "none" }}
                    onChange={onSelectVideo}
                />
                <ImageButton>Select Video</ImageButton>
            </ImagePickerLabel>
            
            {/* Cropper View */}
            <OriginalVideoCropper
                videoSrcInternal={videoSrcInternal}
                crop={crop}
                zoom={zoom}
                setCrop={setCrop}
                setZoom={setZoom}
                computedAspect={computedAspect}
                handleCropComplete={handleCropComplete}
                handleCropSizeChange={handleCropSizeChange}
                videoRef={videoRef}
                currentTime={currentTime}
                duration={duration}
                isPlaying={isPlaying}
                trimStart={trimStart}
                setTrimStart={setTrimStart}
                trimEnd={trimEnd}
                setTrimEnd={setTrimEnd}
                showTrimmer={showTrimmer}
                setShowTrimmer={setShowTrimmer}
                isTrimming={isTrimming}
                handleTrim={handleTrim}
                capturedFrames={capturedFrames}
                progressBarRef={progressBarRef}
                hiddenVideoRef={hiddenVideoRef}
                formatTime={formatTime}
                handleTrimDrag={handleTrimDrag}
                togglePlay={togglePlay}
                trimmedVideoSrc={trimmedVideoSrc}
                cropperKey={cropperKey}
                onCancelTrim={() => {
                    
                    if (videoRef.current) {
                        videoRef.current.pause();
                        videoRef.current.currentTime = 0;
                    }
                    
                    setIsPlaying(false);
                    setCurrentTime(0);
                    setTrimStart(0);
                    setTrimmedVideoSrc(null);
                }}
            />
         
            {/* Crop Overlay */}
            <CropOverlay
                width={cropBoxSize?.width}
                height={cropBoxSize?.height}
            />
        </CropperWrapper>
    );
};
